import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import { catchError, Observable, of, throwError } from 'rxjs';
import { environment } from '../../environments/environment.development';
import { Organisation, Exercise, News, Leader, FriendshipRaceLogin, UploadImageFileRequestBody, UploadExerciseImageFileRequestBody, SkiTrail, Administrator, Cold50RaceLogin } from '../../models/types';
import { jwtDecode } from 'jwt-decode';
import { LoginService } from './login.service';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { MessageProductOwnerComponent } from '../components/message-product-owner/message-product-owner.component';
import { ProductDetailsModalComponent } from '../components/product-details-modal/product-details-modal.component';
import { AddEventComponent } from '../components/add-event/add-event.component';
import { ExerciseModalPageComponent } from '../components/exercise-modal-page/exercise-modal-page.component';
import { Hot100Login } from '../../models/types';
import { NgxImageCompressService } from 'ngx-image-compress';
import { Cold50PrijavaComponent } from '../components/cold50-prijava/cold50-prijava.component';

@Injectable({
  providedIn: 'root'
})
export class OrganisationServiceService {

  constructor(
    private http: HttpClient, 
    private loginService: LoginService,
    private modalService: NgbModal,
    private imageCompress: NgxImageCompressService) { }

    getOrganisation(organisationID: number): Observable<HttpResponse<Organisation>> {
      return this.http.get<Organisation>(`${environment.getOrganisationByIdURL}/${organisationID}`, {
          observe: 'response'
      });
    }

    getOrganisationIdByName(organisationName: string): Observable<HttpResponse<{ organisationID: number }>> {
      const params = { name: organisationName };
      return this.http.get<{ organisationID: number }>(`${environment.getOrganisationByNameURL}`, {
        params,
        observe: 'response'
      });
    }

    openExerciseModal(exercise: Exercise) {
      const modalRef = this.modalService.open(ExerciseModalPageComponent, { size: 'xl' });
      modalRef.componentInstance.exercise = exercise;
    }

    getPaginatedNews(page: number, limit: number, organisationID?: String): Observable<{ news: News[], total: number, page: number, limit: number }> {
      let url = `${environment.newsURL}?page=${page}&limit=${limit}`;
      if (organisationID) {
        url += `&organisationID=${organisationID}`;
      }
      console.log('Fetching paginated news from URL:', url); // Log the URL for debugging
      return this.http.get<{ news: News[], total: number, page: number, limit: number }>(url).pipe(
        catchError((error) => {
          console.error('Error fetching paginated news:', error);
          return throwError(() => new Error('Error fetching paginated news'));
        })
      );
    }
    
    
    addNews(news: News): Observable<any> {
      // Check if user is logged in and token is valid
      if (this.loginService.IsUserLoggedIn() && this.loginService.IsValidToken()) {
        // Send the news data to the backend
        return this.http.post<any>(`${environment.newsURL}`, news)
          .pipe(
            catchError((error) => {
              console.error('Error adding news:', error);
              return of({ message: 'Error adding news' }); // Return an empty object in case of error
            })
          );
      } else {
        console.log("You must be logged in to add news");
        return of({ message: 'You must be logged in to add news' }); // Return a message for the user if not logged in
      }
    }

    updateNews(news: News): Observable<any> {
      if (this.loginService.IsUserLoggedIn() && this.loginService.IsValidToken()) {
        const url = `${environment.newsURL}/${news.newsID}`;

        return this.http.put<any>(url, news).pipe(
          catchError((error) => {
            console.error('Error updating news:', error);
            return of({ message: 'Error updating news' });
          })
        );
      } else {
        console.log('You must be logged in to update news');
        return of({ message: 'You must be logged in to update news' });
      }
    }


    uploadNewsImage(newsID: number, image: File): Observable<any> {
      const formData = new FormData();
      formData.append('image', image);
    
      return this.http.post(`${environment.newsURL}/${newsID}/images`, formData, {
        headers: {
          ...(this.loginService.IsValidToken() && { 'authorization': this.loginService.GetToken()! })
        },
        observe: 'response', // We observe the full HTTP response
      }).pipe(
        catchError((error) => {
          console.error('Error uploading news image:', error);
          return throwError(() => new Error('Error uploading news image')); // Capture and throw error if failed
        })
      );
    }

    updateNewsImage(newsID: number, image: File): Observable<any> {
      const formData = new FormData();
      formData.append('image', image);

      return this.http.put(`${environment.newsURL}/${newsID}/images`, formData, {
        headers: {
          ...(this.loginService.IsValidToken() && { 'authorization': this.loginService.GetToken()! })
        },
        observe: 'response'
      });
    }

    enrollInRace(userData: Hot100Login): Observable<any> {
      const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
  
      return this.http.post<any>(environment.hot100loginURL, userData, { headers }).pipe(
        catchError((error) => {
          console.error('Error submitting race registration:', error);
          return throwError(() => new Error('Failed to register for the race. Please try again.'));
        })
      );
    }

    enrollInFriendshipRace(userData: FriendshipRaceLogin): Observable<any> {
      const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
  
      return this.http.post<any>(environment.friendshipRaceURL, userData, { headers }).pipe(
        catchError((error) => {
          console.error('Error submitting race registration:', error);
          return throwError(() => new Error('Failed to register for the race. Please try again.'));
        })
      );
    }

    getRacers(): Observable<any> {
      return this.http.get<any>(environment.hot100loginURL).pipe(
        catchError((error) => {
          console.error('Error fetching racers:', error);
          return throwError(() => new Error('Failed to fetch racers.'));
        })
      );
    }

    getCold50Racers(): Observable<Cold50RaceLogin[]> {
      return this.http.get<Cold50RaceLogin[]>(environment.cold50URL).pipe(
        catchError((error) => {
          console.error('Error fetching Cold 50 racers:', error);
          return throwError(() => new Error('Failed to fetch Cold 50 racers.'));
        })
      );
    }

    getFriendshipRaceRacers(): Observable<any> {
      return this.http.get<any>(environment.friendshipRaceURL).pipe(
        catchError((error) => {
          console.error('Error fetching friendship racers:', error);
          return throwError(() => new Error('Failed to fetch friendship racers.'));
        })
      );
    }

    getKids(): Observable<any> {
      return this.http.get<any>(environment.varstvoURL).pipe(
        catchError((error) => {
          console.error('Error fetching kids:', error);
          return throwError(() => new Error('Failed to fetch kids.'));
        })
      );
    }

    enrollInVarstvo(userData: any): Observable<any> {
      const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
  
      return this.http.post<any>(environment.varstvoURL, userData, { headers }).pipe(
        catchError((error) => {
          console.error('Error submitting varstvo registration:', error);
          return throwError(() => new Error('Failed to register for varstvo. Please try again.'));
        })
      );
    }

    enrollInCold50Race(userData: Cold50RaceLogin): Observable<any> {
      const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
      return this.http.post<any>(environment.cold50URL, userData, { headers }).pipe(
        catchError((error) => {
          console.error('Error submitting Cold 50 race registration:', error);
          return throwError(() => new Error('Failed to register for the Cold 50 race. Please try again.'));
        })
      );
    }

    downloadVarstvoCSV(): Observable<Blob> {
      return this.http.get(environment.varstvoCsvURL, { responseType: 'blob' });
    }

    downloadCSV(): Observable<Blob> {
      return this.http.get(environment.hot100csvURL, { responseType: 'blob' });
    }

    downloadFriendshipRaceCSV(): Observable<Blob> {
      return this.http.get(environment.friendshipRaceCsvURL, { responseType: 'blob' });
    }

    downloadCold50csv(): Observable<Blob> {
      return this.http.get(environment.cold50csvURL, { responseType: 'blob' });
    }

    deleteNews(newsID: number): Observable<any> {
      if (this.loginService.IsUserLoggedIn() && this.loginService.IsValidToken()) {
      return this.http.delete<any>(`${environment.newsURL}/${newsID}`, {
        headers: {
        'authorization': this.loginService.GetToken()!
        }
      }).pipe(
        catchError((error) => {
        console.error('Error deleting news:', error);
        return of({ message: 'Error deleting news' });
        })
      );
      } else {
      console.log("You must be logged in to delete news");
      return of({ message: 'You must be logged in to delete news' });
      }
    }
    
  addLeader(leader: Leader): Observable<any> {
    if (this.loginService.IsUserLoggedIn() && this.loginService.IsValidToken()) {
      return this.http.post<any>(`${environment.leadersURL}`, leader, {
        headers: {
          'authorization': this.loginService.GetToken()!
        }
      }).pipe(
        catchError((error) => {
          console.error('Error adding leader:', error);
          return of({ message: 'Error adding leader' });
        })
      );
    } else {
      console.log("You must be logged in to add leader");
      return of({ message: 'You must be logged in to add leader' });
    }
  }

  deleteLeader(leaderID: number): Observable<any> {
    if (this.loginService.IsUserLoggedIn() && this.loginService.IsValidToken()) {
      return this.http.delete<any>(`${environment.leadersURL}/${leaderID}`, {
        headers: {
          'authorization': this.loginService.GetToken()!
        }
      }).pipe(
        catchError((error) => {
          console.error('Error deleting leader:', error);
          return of({ message: 'Error deleting leader' });
        })
      );
    } else {
      console.log("You must be logged in to delete leader");
      return of({ message: 'You must be logged in to delete leader' });
    }
  }

  deleteOrgImage(orgId: number, imageSrc: string): Observable<HttpResponse<any>> {
    // Extract the relative path from the full URL
    const imageRef = new URL(imageSrc).pathname.replace('/static/', 'static/');  // This will remove the base URL part and keep only the relative path.
  
    return this.http.delete<any>(environment.orgImagesURL, {
      params: { imageRef },
      headers: {
        ...(this.loginService.IsValidToken() && { 'Authorization': this.loginService.GetToken()! })
      },
      observe: 'response'
    });
  }
  

  getOrganisationImages(organisationID: number): Observable<HttpResponse<{ images: string[] }>> {
    return this.http.get<{ images: string[] }>(`${environment.orgImagesURL}?orgID=${organisationID}`, {
      observe: 'response'
    });
  }

  addOrgImages(organisation: Organisation, images: File[]): Observable<any> {
    const formData = new FormData();
    formData.append('orgID', organisation.organisationID.toString());
    images.forEach((image) => {
      formData.append('images', image);
    });
    return this.http.put(environment.orgImagesURL, formData, {
      headers: {
        ...(this.loginService.IsValidToken() && { authorization: this.loginService.GetToken()! })
      },
      observe: 'response'
    });
  }

  sendHallReservation(data: any): Observable<any> {
    return this.http.post<any>(environment.hallReservationsURL, data).pipe(
      catchError((error) => {
        console.error('Error sending hall reservations:', error);
        return throwError(() => new Error('Failed to send hall reservations.'));
      })
    );
  }

  sendVanRental(data: any): Observable<any> {
    return this.http.post<any>(environment.vanRentalURL, data).pipe(
      catchError((error) => {
        console.error('Error sending van rental:', error);
        return throwError(() => new Error('Failed to send van rental.'));
      })
    );
  }

  getReports(orgId: number): Observable<any> {
    return this.http.get<any>(`${environment.reportsURL}/${orgId}`).pipe(
      catchError((error) => {
        console.error('Error fetching reports:', error);
        return throwError(() => new Error('Failed to fetch reports.'));
      })
    );
  }

  uploadReport(orgId: number, file: File, raceData = false): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('raceData', raceData ? 'true' : 'false');
    return this.http.post(`${environment.reportsURL}/${orgId}/upload`, formData, {
      headers: {
        ...(this.loginService.IsValidToken() && { authorization: this.loginService.GetToken()! })
      },
      observe: 'response'
    });
  }

  deleteReport(reportId: number): Observable<any> {
    return this.http.delete<any>(`${environment.reportsURL}/${reportId}`, {
      headers: {
        'authorization': this.loginService.GetToken()!
      }});
  }

  getExercises(): Observable<Exercise[]> {
    return this.http.get<Exercise[]>(environment.exercisesURL).pipe(
      catchError((error) => {
        console.error('Error fetching exercises:', error);
        return throwError(() => new Error('Error fetching exercises'));
      })
    );
  }

  addExercise(exercise: Exercise): Observable<any> {
    if (this.loginService.IsUserLoggedIn() && this.loginService.IsValidOrgAdminToken("sport")) {
      return this.http.post<any>(environment.exercisesURL, exercise, {
        headers: {
          'authorization': this.loginService.GetToken()!
        }
      }).pipe(
        catchError((error) => {
          console.error('Error adding exercise:', error);
          return of({ message: 'Error adding exercise' });
        })
      );
    } else {
      console.log("You must be logged in to add exercise");
      return of({ message: 'You must be logged in to add exercise' });
    }
  }

  uploadImageFile(data: UploadExerciseImageFileRequestBody): Observable<any> {
    const formData = new FormData();
    formData.append('picture', data.picture);
    formData.append('exerciseID', data.exerciseID.toString());
    return this.http.post<any>(`${environment.exercisesURL}/images`, formData, {
      headers: {
        'authorization': this.loginService.GetToken()!
      }
    }).pipe(
      catchError((error) => {
        console.error('Error uploading exercise image:', error);
        return of({ message: 'Error uploading exercise image' });
      })
    );
  }

  updateExerciseImage(data: UploadExerciseImageFileRequestBody): Observable<any> {
    console.log('Updating exercise image with data:', data);
    const formData = new FormData();
    formData.append('picture', data.picture);
    formData.append('exerciseID', data.exerciseID.toString());
    return this.http.put<any>(`${environment.exercisesURL}/${data.exerciseID}/images`, formData, {
      headers: {
        'authorization': this.loginService.GetToken()!
      }
    }).pipe(
      catchError((error) => {
        console.error('Error updating exercise image:', error); 
        return of({ message: 'Error updating exercise image' });
      })
    );
  }

  deleteExercise(exerciseID: number): Observable<any> {
    if (this.loginService.IsUserLoggedIn() && this.loginService.IsValidOrgAdminToken("sport")) {
      return this.http.delete<any>(`${environment.exercisesURL}/${exerciseID}`, {
        headers: {
          'authorization': this.loginService.GetToken()!
        }
      }).pipe(
        catchError((error) => {
          console.error('Error deleting exercise:', error);
          return of({ message: 'Error deleting exercise' });
        })
      );
    } else {
      console.log("You must be logged in to delete exercise");
      return of({ message: 'You must be logged in to delete exercise' });
    }
  }

  updateExercise(exercise: Exercise): Observable<any> {
    if (this.loginService.IsUserLoggedIn() && this.loginService.IsValidOrgAdminToken("sport")) {
      return this.http.put<any>(`${environment.exercisesURL}/${exercise.exerciseID}`, exercise, {
        headers: {
          'authorization': this.loginService.GetToken()!
        }
      }).pipe(
        catchError((error) => {
          console.error('Error updating exercise:', error);
          return of({ message: 'Error updating exercise' });
        })
      );
    } else {
      console.log("You must be logged in to update exercise");
      return of({ message: 'You must be logged in to update exercise' });
    }
  }

  compressImage(file: File, initialCompression: number, sizeOfOriginal: number): Promise<File> {
    const MAX_SIZE_BYTES = 2 * 1024 * 1024; // 2MB
    const MIN_COMPRESSION = 10; // minimum compression percentage (to avoid too low quality)

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);

      reader.onload = async (event) => {
        const imgBase64 = event.target?.result as string;
        let compression = initialCompression;

        const compress = async (): Promise<File> => {
          try {
            const compressedBase64 = await this.imageCompress.compressFile(imgBase64, -1, compression, sizeOfOriginal);
            // Convert base64 to File
            const byteString = atob(compressedBase64.split(',')[1]);
            const arrayBuffer = new ArrayBuffer(byteString.length);
            const uint8Array = new Uint8Array(arrayBuffer);

            for (let i = 0; i < byteString.length; i++) {
              uint8Array[i] = byteString.charCodeAt(i);
            }

            const compressedFile = new File([uint8Array], file.name, { type: 'image/jpeg' });

            if (compressedFile.size <= MAX_SIZE_BYTES || compression <= MIN_COMPRESSION) {
              return compressedFile;
            } else {
              // Reduce compression quality and retry
              compression -= 10;
              return compress();
            }
          } catch (error) {
            throw error;
          }
        };

        try {
          const finalFile = await compress();
          resolve(finalFile);
        } catch (err) {
          reject(err);
        }
      };

      reader.onerror = error => reject(error);
    });
  }


  getSkiTrails(): Observable<SkiTrail[]> {
    return this.http.get<SkiTrail[]>(environment.skiTrailsURL).pipe(
      catchError((error) => {
        console.error('Error fetching ski trails:', error);
        return throwError(() => new Error('Error fetching ski trails'));
      })
    );
  }

  addSkiTrail(skiTrail: SkiTrail): Observable<any> {
    if (this.loginService.IsUserLoggedIn() && this.loginService.IsValidOrgAdminToken("sport")) {
      return this.http.post<any>(environment.skiTrailsURL, skiTrail, {
        headers: {
          'authorization': this.loginService.GetToken()!
        }
      }).pipe(
        catchError((error) => {
          console.error('Error adding ski trail:', error);
          return of({ message: 'Error adding ski trail' });
        })
      );
    } else {
      console.log("You must be logged in to add ski trail");
      return of({ message: 'You must be logged in to add ski trail' });
    }
  }

  deleteSkiTrail(skiTrailID: number): Observable<any> {
    if (this.loginService.IsUserLoggedIn() && this.loginService.IsValidOrgAdminToken("sport")) {
      return this.http.delete<any>(`${environment.skiTrailsURL}/${skiTrailID}`, {
        headers: {
          'authorization': this.loginService.GetToken()!
        }
      }).pipe(
        catchError((error) => {
          console.error('Error deleting ski trail:', error);
          return of({ message: 'Error deleting ski trail' });
        })
      );
    } else {
      console.log("You must be logged in to delete ski trail");
      return of({ message: 'You must be logged in to delete ski trail' });
    }
  }

  toggleSkiTrailAvailability(skiTrailID: number): Observable<any> {
    if (this.loginService.IsUserLoggedIn() && this.loginService.IsValidOrgAdminToken("sport")) {
      console.log("Toggling ski trail availability for ID:", skiTrailID);
      return this.http.post<any>(`${environment.skiTrailsURL}/${skiTrailID}/toggle`, {}, {
        headers: {
          'authorization': this.loginService.GetToken()!
        }
      }).pipe(
        catchError((error) => {
          console.error('Error toggling ski trail availability:', error);
          return of({ message: 'Error toggling ski trail availability' });
        })
      );
    } else {
      console.log("You must be logged in to toggle ski trail availability");
      return of({ message: 'You must be logged in to toggle ski trail availability' });
    }
  }

  addOrganisation(organisation: Organisation): Observable<any> {
    if (this.loginService.IsUserLoggedIn() && this.loginService.IsValidToken()) {
      return this.http.post<Organisation>(environment.organisationURL, organisation, {
        headers: {
          'authorization': this.loginService.GetToken()!
        }
      }).pipe(
        catchError((error) => {
          console.error('Error adding organisation:', error);
          return of({ message: 'Error adding organisation' });
        })
      );
    } else {
      console.log("You must be logged in to add organisation");
      return of({ message: 'You must be logged in to add organisation' });
    }
  }

  updateOrganisation(organisation: any): Observable<any> {
    console.log('Updating organisation:', organisation);
    if (this.loginService.IsUserLoggedIn() && this.loginService.IsValidToken()) {
      return this.http.put<Organisation>(`${environment.organisationURL}/${organisation.id}`, organisation, {
        headers: {
          'authorization': this.loginService.GetToken()!
        }
      }).pipe(
        catchError((error) => {
          console.error('Error updating organisation:', error);
          return of({ message: 'Error updating organisation' });
        })
      );
    } else {
      console.log("You must be logged in to update organisation");
      return of({ message: 'You must be logged in to update organisation' });
    }
  }

  deleteOrganisation(organisationID: number): Observable<any> {
    if (this.loginService.IsUserLoggedIn() && this.loginService.IsValidToken()) {
      return this.http.delete<any>(`${environment.organisationURL}/${organisationID}`, {
        headers: {
          'authorization': this.loginService.GetToken()!
        }
      }).pipe(
        catchError((error) => {
          console.error('Error deleting organisation:', error);
          return of({ message: 'Error deleting organisation' });
        })
      );
    } else {
      console.log("You must be logged in to delete organisation");
      return of({ message: 'You must be logged in to delete organisation' });
    }
  }

  getAdministrators(): Observable<any> {
    if (this.loginService.IsUserLoggedIn() && this.loginService.IsValidToken()) {
      return this.http.get<any>(environment.administratorsURL, {
        headers: {
          'authorization': this.loginService.GetToken()!
        }
      }).pipe(
        catchError((error) => {
          console.error('Error fetching administrators:', error);
          return of({ message: 'Error fetching administrators' });
        })
      );
    } else {
      console.log("You must be logged in to fetch administrators");
      return of({ message: 'You must be logged in to fetch administrators' });
    }
  }

  addAdministrator(admin: Administrator): Observable<any> {
    if (this.loginService.IsUserLoggedIn() && this.loginService.IsValidToken()) {
      return this.http.post<any>(environment.administratorsURL, admin, {
        headers: {
          'authorization': this.loginService.GetToken()!
        }
      }).pipe(
        catchError((error) => {
          console.error('Error adding administrator:', error);
          return of({ message: 'Error adding administrator' });
        })
      );
    } else {
      console.log("You must be logged in to add administrator");
      return of({ message: 'You must be logged in to add administrator' });
    }
  }

  deleteAdministrator(adminID: number): Observable<any> {
    if (this.loginService.IsUserLoggedIn() && this.loginService.IsValidToken()) {
      return this.http.delete<any>(`${environment.administratorsURL}/${adminID}`, {
        headers: {
          'authorization': this.loginService.GetToken()!
        }
      }).pipe(
        catchError((error) => {
          console.error('Error deleting administrator:', error);
          return of({ message: 'Error deleting administrator' });
        })
      );
    } else {
      console.log("You must be logged in to delete administrator");
      return of({ message: 'You must be logged in to delete administrator' });
    }
  }

}
